import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useGenerateOtpMutation,
  useLoginMutation,
} from "../../../store/api/authApi";
import {
  getCookies,
  handleStoreCookie,
  setCookies,
} from "../../../helpers/storage";
import { STORAGE } from "../../../constants";
import { AUTH_PHONE_OTP_SCHEMA } from "../../../helpers/validation-schema";
import { ROUTES } from "../../../constants/routes";
import Input from "../../../components/common/input/input";
import Button from "../../../components/common/button";
import AuthLayout from "../../../components/layouts/auth";
import LottieAnimation from "../../../components/common/animations";
import "./index.css";

const AuthPhoneOTP = () => {
  const [shakeInputs, setShakeInputs] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendTimer, setOtpTimer] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  // Extract query parameters using URLSearchParams
  const queryParams = new URLSearchParams(location.search);

  const phoneNo = queryParams.get("phone");

  const [login] = useLoginMutation();
  const [generateOtp] = useGenerateOtpMutation();

  type OTPFormData = {
    otp: string;
  };

  useEffect(() => {
    const currentTimerValue = Number(getCookies(STORAGE.OTP_TIMER)) || 0;
    setOtpTimer(Number(currentTimerValue));
  }, []);

  useEffect(() => {
    handleOTPResendTimer(resendTimer);
  }, [resendTimer]);

  const handleOTPResendTimer = (time: number) => {
    if (time) {
      setTimeout(() => {
        const timer = resendTimer - 1;
        const updatedTimer = timer < 10 ? `0${timer}` : timer;
        setOtpTimer(Number(updatedTimer));
        setCookies(STORAGE.OTP_TIMER, updatedTimer);
      }, 1000);
    }
  };

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm<OTPFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(AUTH_PHONE_OTP_SCHEMA),
    defaultValues: {
      otp: "",
    },
  });

  const onSubmit = async (data: OTPFormData) => {
    const response: any = await login({
      otp: data?.otp,
      phone: phoneNo,
    });
    if (response?.data?.success) {
      // trackEvent(MixpanelEvent.OTP_VERIFICATION_SUBMITTED, {
      //   otp: data?.otp,
      //   phone: phoneNo,
      //   verification_state: "success",
      // });
      const { access_token, refresh_token } = response?.data?.data || {};
      handleStoreCookie(access_token, refresh_token);
      navigate(ROUTES.HOME);
    } else {
      // trackEvent(MixpanelEvent.OTP_VERIFICATION_SUBMITTED, {
      //   otp: data?.otp,
      //   phone: phoneNo,
      //   verification_state: "failed",
      // });
      setErrorMessage("OTP incorrect, please try again.");
      setShakeInputs(true);
      setTimeout(() => setShakeInputs(false), 1000);
    }
  };

  const otpValue: string = watch("otp") || "";
  useEffect(() => {
    if (otpValue.length === 4) {
      handleSubmit(onSubmit)();
    }
  }, [otpValue]);

  const handleOTPChange = (value: string, index: number) => {
    if (/^[0-9]$/.test(value) || value === "") {
      const newOtp = otpValue.split("");
      newOtp[index] = value;
      const otpString = newOtp.join("");
      setValue("otp", otpString);
      if (value !== "" && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      } else if (value === "" && index > 0) {
        const prevInput = document.getElementById(`otp-${index - 1}`);
        if (prevInput) prevInput.focus();
      }
    }
  };

  const resendOTP = async () => {
    const response: any = await generateOtp({ phone: phoneNo });
    if (response?.data?.success) {
      setOtpTimer(30);
      setCookies(STORAGE.OTP_TIMER, 30);
    }
  };

  return (
    <AuthLayout>
      <form className="flex w-full h-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="h-full flex flex-col justify-between">
          <div>
            <div className="w-32 md:mb-10 mb-6">
              <LottieAnimation
                lottieUrl="/animation/Logo.lottie"
                height="70px"
              />
            </div>
            <div className="text-3xl text-white leading-10 font-bold mb-6">
              Enter the code sent to you at +{phoneNo}
            </div>
            <div className="flex items-start">
              {[...Array(4)].map((_, index) => (
                <Input
                  key={index}
                  id={`otp-${index}`}
                  type="number"
                  maxLength={1}
                  className={`md:w-24 w-16 h-12 text-center rounded-md mr-1 bg-[#262626] text-white outline-none focus:placeholder-transparent ${
                    shakeInputs ? "border-2 border-[#EE4D37] animate-shake" : ""
                  }`}
                  value={watch("otp")?.[index] || ""}
                  onChange={(value: string) => handleOTPChange(value, index)}
                  placeholder="0"
                />
              ))}
            </div>
            {errorMessage ? (
              <div className="text-xs text-[#EE4D37] my-3">{errorMessage}</div>
            ) : null}
            {errors?.otp?.message && otpValue?.length < 4 ? (
              <div className="text-error text-xs my-3">
                {errorMessage}
                {errors?.otp?.message}
              </div>
            ) : null}
            <div>{}</div>
            <div className="text-gray-400 text-xs mt-3 flex">
              Didn’t receive it?{" "}
              {resendTimer ? (
                `Retry in 00:${resendTimer}`
              ) : (
                <div
                  onClick={resendOTP}
                  className="text-blue-500 cursor-pointer hover:text-blue-600"
                >
                  &nbsp; Resend
                </div>
              )}
            </div>
          </div>
          <div>
            <Button
              isSubmitting={isSubmitting}
              customClasses="w-full mb-5 rounded-xl"
            >
              Next
            </Button>
            <div className="text-gray-400 text-xs justify-center items-center flex flex-wrap text-center gap-1">
              By clicking, I accept the
              <a
                href="https://www.onepercentclub.io/terms-conditions"
                className="text-white"
              >
                Terms & Conditions
              </a>
              &
              <a
                href="https://www.onepercentclub.io/privacy-policy"
                className="text-white"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AuthPhoneOTP;
